import React, { useEffect } from 'react';
import type { VideoMetaData, VideoMetrics } from './types';
import { useVideoEventHandlers } from './useVideoEventHandlers';

type VideoProps = {
  alt?: string;
  url: string;
  className?: string;
  onLoadedMetadata?: (duration: number) => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onError?: (error?: MediaError) => void;
  onBuffering?: () => void;
  onEnded?: (metrics: VideoMetrics, metadata: VideoMetaData) => void;
};

export const Video: React.FC<VideoProps> = React.memo(
  ({ alt, url, className, onLoadedMetadata, onTimeUpdate, onError, onBuffering, onEnded }) => {
    const { ref: videoRef } = useVideoEventHandlers({
      onTimeUpdate,
      onLoadedMetadata,
      onError,
      onBuffering,
      onEnded,
    });

    useEffect(() => {
      const videoElement = videoRef.current;

      const playVideo = () => {
        if (videoElement && videoElement.paused) {
          videoElement.load();
          videoElement.play().catch(() => {
            console.error('Error automatically playing video');
          });
        }
      };

      if (videoElement) {
        playVideo();
      }

      return () => {
        if (videoElement) {
          videoElement.pause();
          videoElement.currentTime = 0;
          videoElement.load(); // this resets the video element
        }
      };
    }, [videoRef]);

    return (
      <video
        ref={videoRef}
        className={className}
        title={alt || 'Native video content'}
        src={url}
        controls
        autoPlay={true}
        loop
        playsInline
        onClick={(e) => e.stopPropagation()}
        controlsList="nodownload nofullscreen"
      />
    );
  },
);

Video.displayName = 'Video';
